'use client';

import type { PropsWithChildren } from 'react';
import React, { useRef } from 'react';
import type { Key } from 'react-aria';
import clsx from 'clsx';
import type { TextProps } from 'components/Text';
import { Text } from 'components/Text';
import { useSize } from '../../hooks/useSize';
import type { ListVariants, SetSizeWithId } from '../types';
import styles from './ListHeader.module.css';

type ListHeaderProps = {
  fixedHeight?: number;
  nodeId: Key;
  updateItemSize: SetSizeWithId;
  isSticky?: boolean;
  variant: ListVariants;
  isPinned?: boolean;
  backgroundColor?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export const ListHeader = React.memo(
  ({
    children,
    nodeId,
    updateItemSize,
    fixedHeight,
    isSticky = true,
    variant,
    isPinned = false,
    backgroundColor = 'transparent',
    className,
    ...other
  }: PropsWithChildren<ListHeaderProps>) => {
    const ref = useRef(null);

    useSize(
      ref,
      (size) => {
        updateItemSize(nodeId, size);
      },
      { enabled: !fixedHeight }
    );

    return (
      <div
        ref={ref}
        className={clsx(styles.listHeader, className)}
        data-pinned={isPinned || undefined}
        data-sticky={isSticky || undefined}
        data-variant={variant}
        style={{
          height: fixedHeight,
          background: backgroundColor,
        }}
        {...other}
      >
        {typeof children === 'string' ? (
          <ListHeaderText>{children}</ListHeaderText>
        ) : (
          children
        )}
      </div>
    );
  }
);

export const ListHeaderText = (props: TextProps) => (
  <Text color="tertiary" truncate variant="secondary" {...props} />
);

export const headerVariants: Record<string, { isHeaderSticky: boolean | undefined; headerHeight: number | undefined }> = {
  unstyled: {
    isHeaderSticky: undefined,
    headerHeight: undefined,
  },
  nav: {
    isHeaderSticky: false,
    headerHeight: 28,
  },
  list: {
    isHeaderSticky: true,
    headerHeight: 40,
  },
};
